import type { BoostTAEConfig } from '@boost/widget-integration-tae/lib';
import { getBoostTAEConfig } from '@boost/widget-integration-tae/lib';
import { HTTPService } from 'framework/services';

import type { PlatformLoader } from '../platform/platform.loader';

export abstract class BoostAPI extends HTTPService {
  DEFAULT_LOCALE = 'en';
  TAEAppConfig: BoostTAEConfig;

  constructor(protected platformLoader: PlatformLoader) {
    super();
    this.TAEAppConfig = getBoostTAEConfig();
  }

  buildDefaultQueryParams(
    params: Record<string, string | number | boolean | Array<string | number | boolean>>,
    options?: {
      includeSearchQuery?: boolean;
    }
  ) {
    const { includeSearchQuery = true } = options || {};
    const queryParams = new URLSearchParams();
    queryParams.sort();
    for (const key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key)) {
        const value = params[key];
        if (Array.isArray(value)) {
          value.forEach((item) => {
            queryParams.append(`${key}[]`, item.toString());
          });
        } else {
          queryParams.set(key, value.toString());
        }
      }
    }

    const urlParams = new URLSearchParams(window.location.search);
    const searchQuery = urlParams.get('q');

    if (searchQuery && includeSearchQuery) {
      queryParams.set('q', searchQuery);

      if (this.platformLoader.platform.router.page === 'collection') {
        queryParams.set('incollection_search', 'true');
        queryParams.set('event_type', 'incollection_search');
      }
    }

    const customerId = this.platformLoader.platform.customer?.id;

    if (customerId) {
      queryParams.set('customer_id', customerId);
    }

    return queryParams;
  }

  buildEndpointWithLocale(endpoint: string): string {
    const { router } = this.platformLoader.platform;
    if (router.routes?.root) {
      return `${router.routes.root}${endpoint}`;
    }

    return endpoint;
  }

  addCurrencyParams<P extends Record<string, unknown>>(params: P): P & CurrencyParams {
    const { localization, currency } = this.platformLoader.platform;

    const currencyParams: CurrencyParams = {
      return_all_currency_fields: false,
      locale: localization?.locale || this.DEFAULT_LOCALE,
      currency_rate: currency?.rate,
      currency: window.Shopify?.currency?.active,
      country: localization?.country,
    };

    return {
      ...params,
      ...currencyParams,
    };
  }

  addTenantParams<P extends Record<string, unknown>>(
    params: P
  ): P & { shop?: string; money_format?: string; money_format_with_currency?: string } {
    const shopConfig = this.TAEAppConfig.shop || {};
    const shop = shopConfig.domain || '';
    const moneyFormat = shopConfig.money_format || '${{amount}}';
    const moneyFormatWithCurrency = shopConfig.money_format_with_currency || '${{amount}} USD';

    return {
      ...params,
      shop,
      money_format: moneyFormat,
      money_format_with_currency: moneyFormatWithCurrency,
    };
  }

  addLocalizationParams<P extends Record<string, unknown>>(params: P): P & { locale?: string } {
    const { localization } = this.platformLoader.platform;

    if (localization?.locale) {
      return {
        ...params,
        locale: localization.locale,
      };
    }

    return params;
  }

  addCustomerIdParam<P extends Record<string, unknown>>(
    params: P
  ): P & { customer_id?: string | number } {
    const customerId = this.platformLoader.platform.customer?.id;

    if (customerId) {
      return {
        ...params,
        customer_id: customerId,
      };
    }

    return params;
  }
}

export type CurrencyParams = {
  return_all_currency_fields?: boolean;
  locale?: string;
  currency_rate?: string;
  currency?: string;
  country?: string;
};
